import '../../styles/CalendarStyles.css';
import './EditUsers.scss';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import chevronLeft from '../../assets/images/icons/chevron-left.svg';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import FormDatePicker from '../../components/FormDatePicker/FormDatePicker';
import InputForm from '../../components/InputForm/InputForm';
import SelectForm from '../../components/SelectForm/SelectForm';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { verificationMissing } from './verificationMissing';
import Button from '../../components/Buttons/Button';

// Keys must match REASONS in the server's photoTakedownNotice.js, which 400s
// on anything it does not know. Nothing is preselected, so no click sends by accident.
const TAKEDOWN_REASONS = [
    { value: 'silent', label: "Don't tell them", hint: 'Takes the photo down quietly. Use this for test accounts.' },
    { value: 'not_you', label: 'Not a real photo of you', hint: 'A screen or TV, someone else, or AI-generated.' },
    { value: 'not_a_person', label: 'Not a photo of a person', hint: 'A logo, a cartoon or an avatar.' },
    { value: 'inappropriate', label: 'Inappropriate', hint: 'Not appropriate for ZONA.' },
];

const EditUsers = () => {
    const { userID } = useParams();
    const navigate = useNavigate();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [birthday, setBirthday] = useState('');
    const [gender, setGender] = useState('');

    const [user, setUser] = useState();

    const [deleteModal, setDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [gallery, setGallery] = useState([]);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [updating, setUpdating] = useState(false);
    // Identity verification (manual admin approval)
    const [verStatus, setVerStatus] = useState('none');
    const [igHandle, setIgHandle] = useState('');
    const [tiktokHandle, setTiktokHandle] = useState('');
    const [verSaving, setVerSaving] = useState(false);
    // null = closed. '' = open, nothing picked yet.
    const [photoReason, setPhotoReason] = useState(null);
    // Set while the reason picker is open for a gallery photo instead of the profile photo.
    const [galleryTarget, setGalleryTarget] = useState(null);
    const [takingDown, setTakingDown] = useState(false);
    // Ban for cause (fraud/spam/ToS), separate from the delete flow above.
    const [banModal, setBanModal] = useState(false);
    const [banLoading, setBanLoading] = useState(false);
    // Discover row visibility (profile-screen teaser only, not the full
    // Discover page or search — see discover_row_hidden on the server).
    const [discoverRowSaving, setDiscoverRowSaving] = useState(false);
    const [loginLinkSending, setLoginLinkSending] = useState(false);
    const [loginLinkResult, setLoginLinkResult] = useState(null);
    // Verified Promoter grant (server: /promoter-requests/grant and /revoke).
    const [promoterSaving, setPromoterSaving] = useState(false);
    // Remove Verified DJ (server: /dj-requests/revoke). Confirm happens in the card.
    const [djConfirm, setDjConfirm] = useState(false);
    const [djRemoving, setDjRemoving] = useState(false);
    // Weekly nights (server: /users/admin/nights/:id). Each night is a plan under this
    // account that reposts weekly; `eligibility` says whether reposts will actually run.
    const [nights, setNights] = useState([]);
    const [nightsEligibility, setNightsEligibility] = useState(null);
    const [nightsLoading, setNightsLoading] = useState(false);
    const [nightSaving, setNightSaving] = useState(false);
    const [nightStopping, setNightStopping] = useState(null);
    const [plansAccessSaving, setPlansAccessSaving] = useState(false);
    const [venueOptions, setVenueOptions] = useState(null);
    const venueOptionsLoading = useRef(false);
    const [nightForm, setNightForm] = useState({ venue: null, venueName: '', weekday: 5, start: '23:00', end: '05:00', closes: '', kind: 'Guestlist', imageUrl: '' });

    const [errors, setErrors] = useState({});

    const validationSchema = Yup.object().shape({
        // Nothing is required, because the server's editUser skips any empty field
        // (only lastName can be cleared). Blank here means "leave it as it is", and
        // requiring values blocked edits to the 5,537 accounts that have no phone.
        firstName: Yup.string().trim(),
        lastName: Yup.string().trim(),
        phone: Yup.string().trim(),
        email: Yup.string().trim().email("Invalid email").matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, { message: 'Invalid email format', excludeEmptyString: true }),
        gender: Yup.string(),
    });

    useEffect(() => {
        const getUser = async () => {
            try {
                const response = await api.get(`/users/${userID}`);
                setUser(response.data.user);
            } catch (err) {
                console.log(`error retrieving user. this is the error: ${err}`);
            }
        };
        getUser();
    }, [userID]);

    const handleFormSubmit = async (event) => {
        event.preventDefault();

        setUpdating(true);
        try {

            await validationSchema.validate({
                firstName,
                lastName,
                phone,
                email,
                gender,
            }, { abortEarly: false });

            // Yup's .trim() only transforms the copy it validates, it does not
            // touch state, so trim here or the whitespace is what gets saved.
            const trimmed = {
                firstName: (firstName || '').trim(),
                lastName: (lastName || '').trim(),
                phone: (phone || '').trim(),
                email: (email || '').trim(),
                gender: gender || '',
                birthday: birthday || '',
            };

            // Only what she actually changed. Sending every field re-ran the
            // server's gibberish check against the stored name on every save, so
            // a flagged account could not have its phone or email corrected at
            // all, and it made each save a rewrite of fields nobody touched.
            const editedUser = { admin: isAdmin };
            for (const [key, value] of Object.entries(trimmed)) {
                const current = user?.[key] ?? '';
                // lastName is the one field the server lets you clear.
                if (value !== current && (value !== '' || key === 'lastName')) {
                    editedUser[key] = value;
                }
            }

            // editUser resolves the Firebase uid only (findByFirebaseUid, no
            // ObjectId fallback), unlike the GET that filled this form.
            const id = user?.id;
            if (!id) throw new Error('This account has no Firebase user, so the panel cannot save changes to it.');
            await api.put(`/users/${id}`, editedUser);

            setFirstName("");
            setLastName("");
            setPhone("");
            setEmail("");
            setGender("");

            toast.success('User updated.');

            navigate("/users");

        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const validationErrors = {};
                error.inner.forEach(err => {
                    validationErrors[err.path] = err.message;
                });
                toast.error(`Error Edit User. Please try again, ${error.message}`);
                setErrors(validationErrors);
            } else {
                // Was console-only. With lastName no longer checked here, the server is
                // what rejects a save, and those messages have to reach her.
                console.error("Error edit user:", error);
                toast.error(error?.response?.data?.message || error?.message || 'Could not save. Please try again.');
            }

        } finally {
            setUpdating(false)
        };
    }

    // Hides/restores the user in the small Discover row teaser shown on
    // OTHER members' profile screens. Leaves them fully visible on the full
    // Discover page and in search — this is one surface, not the account.
    const handleToggleDiscoverRowHidden = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        const nextHidden = !user?.discover_row_hidden;
        setDiscoverRowSaving(true);
        try {
            await api.post(`/users/admin/discover-row-hidden/${idForApi}`, { hidden: nextHidden });
            toast.success(nextHidden ? 'Hidden from the Discover row.' : 'Restored to the Discover row.');
            setUser((prev) => (prev ? { ...prev, discover_row_hidden: nextHidden } : prev));
        } catch (err) {
            toast.error(`Could not update. ${err?.response?.data?.message || err.message}`);
            console.error('setDiscoverRowHidden failed:', err);
        } finally {
            setDiscoverRowSaving(false);
        }
    };

    const handleDeleteUser = (user) => {
        setDeleteModal(true);
        setUserToDelete(user);
    };

    // Bans the account: disables + revokes Firebase Auth, blocks the phone
    // number and device_id from re-signup, stamps banned_at. Distinct from
    // handleDelete above (that's the self-service soft-delete path, which
    // emails a recovery link and would undo this). No email is sent.
    const isVerifiedPromoter = Array.isArray(user?.verified_roles) && user.verified_roles.includes('Verified Promoter');

    const isVerifiedDj = Array.isArray(user?.verified_roles) && user.verified_roles.includes('Verified DJ');
    const djDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || 'this account';
    const djConfirmText = `Remove Verified DJ from ${djDisplayName}? Their DJ tag stops, and their DJ guestlist tools and future weekly reposts stop unless another grant covers them. Their account and past plans stay. Nothing is sent to them.`;

    // Returns false when the fetch failed, so a caller can say the page is stale.
    const reloadUser = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return false;
        try {
            // Same shape the first load reads: { user, check_ins }.
            const res = await api.get(`/users/${idForApi}`);
            setUser(res.data?.user || res.data);
            return true;
        } catch (err) {
            console.error('Reload user failed:', err);
            return false;
        }
    };

    const handlePromoterGrant = async (grant) => {
        if (!user?._id) return;
        setPromoterSaving(true);
        try {
            const res = await api.post(grant ? '/promoter-requests/grant' : '/promoter-requests/revoke', { userId: user._id });
            toast.success(res.data?.message || (grant ? 'Verified Promoter granted.' : 'Verified Promoter removed.'));
            await reloadUser();
            await loadNights();
        } catch (err) {
            toast.error(`Could not update Verified Promoter. ${err?.response?.data?.message || err.message}`);
        } finally {
            setPromoterSaving(false);
        }
    };

    const handleRemoveDj = async () => {
        if (!user?._id || djRemoving) return;
        setDjRemoving(true);
        try {
            const res = await api.post('/dj-requests/revoke', { userId: user._id });
            setDjConfirm(false);
            const [refreshed, nightsLoaded] = await Promise.all([reloadUser(), loadNights()]);
            if (refreshed && nightsLoaded) toast.success(res.data?.message || 'Verified DJ removed.');
            else toast.error('Verified DJ was removed, but this page could not refresh. Reload to see the change.');
        } catch (err) {
            toast.error(`Could not remove Verified DJ. ${err?.response?.data?.message || err.message}`);
        } finally {
            setDjRemoving(false);
        }
    };

    // Returns false when the fetch failed, so a caller can say the page is stale.
    const loadNights = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return false;
        setNightsLoading(true);
        try {
            const res = await api.get(`/users/admin/nights/${idForApi}`);
            setNights(res.data?.nights || []);
            setNightsEligibility(res.data?.eligibility || null);
            return true;
        } catch (err) {
            console.error('Load nights failed:', err);
            return false;
        } finally {
            setNightsLoading(false);
        }
    };

    const loadVenueOptions = async () => {
        // One fetch: hover and focus both call this, so the in-flight flag stops a second
        // full venue list (an expensive call) going out before the first answers.
        if (venueOptions || venueOptionsLoading.current) return;
        venueOptionsLoading.current = true;
        try {
            const res = await api.get('/venues', { params: { records: 99999999, page: 1 } });
            // Live venues: status 'active', or no status at all (older rows), as the app reads them.
            const rows = (res.data?.data || []).filter((v) => v.status === 'active' || v.status === null || v.status === undefined);
            setVenueOptions(rows.map((v) => ({ value: v._id, label: v.name })));
        } catch (err) {
            toast.error('Could not load venues.');
        } finally {
            venueOptionsLoading.current = false;
        }
    };

    // "23:30" -> minutes from midnight, '' -> null, anything else -> undefined (invalid).
    const toMinutes = (hhmm) => {
        if (!hhmm) return null;
        const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
        if (!m) return undefined;
        const h = Number(m[1]); const min = Number(m[2]);
        if (h > 23 || min > 59) return undefined;
        return h * 60 + min;
    };
    const fromMinutes = (v) => (v === null || v === undefined ? '' : `${String(Math.floor(v / 60) % 24).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`);
    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const handleAddNight = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        const start = toMinutes(nightForm.start);
        const end = toMinutes(nightForm.end);
        const closes = toMinutes(nightForm.closes);
        if (start === null || start === undefined || end === undefined || closes === undefined) {
            toast.error('Times must look like 23:00. Start is required.');
            return;
        }
        if (!nightForm.venue && !nightForm.venueName.trim()) {
            toast.error('Pick a venue or type its name.');
            return;
        }
        setNightSaving(true);
        try {
            const res = await api.post(`/users/admin/nights/${idForApi}`, {
                venue_id: nightForm.venue?.value || null,
                venue_name: nightForm.venue ? nightForm.venue.label : nightForm.venueName.trim(),
                weekday: Number(nightForm.weekday),
                start,
                end,
                closes,
                kind: nightForm.kind,
                image_url: nightForm.imageUrl.trim() || null,
            });
            const r = res.data?.result || {};
            if (r.posted?.length) toast.success(`Posted: ${r.posted.map((x) => x.label).join(' · ')}`);
            else if (r.alreadyUp?.length) toast.info('That night already has a plan up. Details updated for next week.');
            else if (r.failed?.length) toast.error(`Could not post: ${r.failed.map((x) => x.error).join('; ')}`);
            setNights(res.data?.nights || []);
            setNightsEligibility(res.data?.eligibility || null);
            setNightForm((f) => ({ ...f, venue: null, venueName: '', imageUrl: '' }));
        } catch (err) {
            toast.error(`Could not add the night. ${err?.response?.data?.message || err.message}`);
        } finally {
            setNightSaving(false);
        }
    };

    const handleStopNight = async (night) => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        setNightStopping(night.id);
        try {
            const res = await api.delete(`/users/admin/nights/${idForApi}/${night.id}`);
            setNights(res.data?.nights || []);
            toast.success(`Stopped ${DAYS[night.weekday]} at ${night.venue_name}. A plan already up stays up.`);
        } catch (err) {
            toast.error(`Could not stop the night. ${err?.response?.data?.message || err.message}`);
        } finally {
            setNightStopping(null);
        }
    };

    const handlePlansAccess = async (enabled) => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        setPlansAccessSaving(true);
        try {
            const res = await api.post(`/users/admin/plans-access/${idForApi}`, { enabled });
            setNightsEligibility(res.data?.eligibility || null);
            toast.success(enabled ? 'Plans enabled for this account.' : 'Plans disabled for this account.');
        } catch (err) {
            toast.error(`Could not change plans access. ${err?.response?.data?.message || err.message}`);
        } finally {
            setPlansAccessSaving(false);
        }
    };

    // Texts a one-time sign-in link from the 866 to the account's mobile number. The
    // link opens the app on a create-password screen (or the web fallback at
    // getzona.app/login/<token>). Older unused links stop working when a new one goes out.
    const handleSendLoginLink = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        setLoginLinkSending(true);
        setLoginLinkResult(null);
        try {
            const res = await api.post(`/users/admin/login-link/${idForApi}`);
            setLoginLinkResult(res.data);
            if (res.data?.sent) toast.success(`Link texted to ${res.data.sent_to}.`);
            else toast.warn(`Link created but the text failed. Copy it below. ${res.data?.send_error || ''}`);
        } catch (err) {
            toast.error(`Could not send the link. ${err?.response?.data?.message || err.message}`);
            console.error('Send login link failed:', err);
        } finally {
            setLoginLinkSending(false);
        }
    };

    const handleBan = async () => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        setBanLoading(true);
        try {
            const res = await api.post(`/users/admin/ban/${idForApi}`, {
                reason: 'Spam/fraud — banned via admin panel',
            });
            setBanModal(false);
            toast.success('User banned.');
            setUser((prev) => (prev ? {
                ...prev,
                banned_at: res.data?.banned_at || new Date().toISOString(),
                ban_reason: 'Spam/fraud — banned via admin panel',
            } : prev));
        } catch (err) {
            toast.error(`Ban failed. ${err?.response?.data?.message || err.message}`);
            console.error('Ban failed:', err);
        } finally {
            setBanLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setIsLoading(true);
        try {
            await api.delete(`/users/${id}`);
            setDeleteModal(false);
            toast.success('User deleted.');

            navigate("/users");
        } catch (err) {
            toast.error(`Error Delete User. Please try again, ${err.message}`);
            console.log(`failed to delete user. this is the error: ${err}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBirthdaySelect = (selectedBirthday) => {
        setBirthday(selectedBirthday)
    };

    const handleCopyId = async (value, label) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied.`);
        } catch (err) {
            toast.error('Could not copy.');
        }
    };

    useEffect(() => {
        // Empty, never '-'. These hydrate the inputs and handleFormSubmit posts them
        // verbatim, so the placeholder was being saved as the value itself.
        setFirstName(user?.firstName || '')
        setLastName(user?.lastName || '')
        setPhone(user?.phone || '')
        setEmail(user?.email || '')
        setBirthday(user?.birthday || '')
        setGender(user?.gender || '')
        setIsAdmin(user?.admin)
        setGallery(user?.gallery || [])
        setVerStatus(user?.verification?.status || (user?.verified ? 'verified' : 'none'))
        setIgHandle(user?.verification?.ig_handle || '')
        setTiktokHandle(user?.verification?.tiktok_handle || '')
        setLoginLinkResult(null)
        setNights([])
        setNightsEligibility(null)
        if (user?._id) loadNights()
        // Keyed on the id, not the whole object: taking a photo down calls
        // setUser, and re-running this wiped unsaved edits and brought deleted
        // gallery rows back on screen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id])

    // Content moderation: take down a gallery photo. Opens the same reason picker
    // as the profile photo, so they're told why (her ask 2026-10-08).
    const handleGalleryRemove = useCallback((galleryId) => {
        if (!galleryId) return;
        setGalleryTarget(galleryId);
        setPhotoReason('');
    }, [])

    const takeDownGalleryPhoto = useCallback(async (galleryId, reason) => {
        setTakingDown(true);
        try {
            const res = await api.delete(`/gallery/admin-management/${galleryId}`, { data: { reason } });
            setGallery((prev) => prev.filter((p) => p.id !== galleryId));
            setPhotoReason(null);
            setGalleryTarget(null);
            const d = res?.data?.takedown;
            if (reason === 'silent') toast.success('Photo taken down. They were not told.');
            else if (d && d.notified === false) toast.warn('Photo taken down, but we could not reach them on any channel.');
            else toast.success('Photo taken down and they have been told why.');
        } catch (e) {
            console.error('Gallery take-down failed', e);
            toast.error(e?.response?.data?.message || 'Could not take down the photo. Please try again.');
        } finally {
            setTakingDown(false);
        }
    }, [])

    // Take down the profile photo (avatar) if it's explicit / violates policy.
    // DELETE /users/photo/:id clears user.photo + removes the file.
    // The dialog stays open until this resolves, so a second click cannot fire
    // a duplicate DELETE and the chosen reason survives a failure.
    const takeDownProfilePhoto = useCallback(async (reason) => {
        // The endpoint looks the account up by _id, so a firebase uid will not do.
        if (!user?._id) {
            toast.error('This user has not finished loading, so the photo cannot be taken down yet.');
            return;
        }
        setTakingDown(true);
        try {
            const res = await api.delete(`/users/photo/${user._id}`, { data: { reason } });
            setUser((prev) => (prev ? { ...prev, photo_url: null } : prev));
            setPhotoReason(null);
            // The server reports whether anything actually reached them, rather
            // than the panel assuming a 200 means they were told.
            const d = res?.data?.takedown;
            if (reason === 'silent') toast.success('Photo taken down. They were not told.');
            else if (d && d.notified === false) toast.warn('Photo taken down, but we could not reach them on any channel.');
            else toast.success('Photo taken down and they have been told why.');
        } catch (e) {
            console.error('Profile photo take-down failed', e);
            toast.error(e?.response?.data?.message || 'Could not take down the profile photo. Please try again.');
        } finally {
            setTakingDown(false);
        }
    }, [user?._id])

    // Manual identity verification: approve/reject + record the handles the
    // admin eyeballed against the user's photos. Sets the verified badge.
    const handleSetVerification = async (status) => {
        const idForApi = user?.id || user?._id;
        if (!idForApi) return;
        setVerSaving(true);
        try {
            const res = await api.put(`/users/${idForApi}/verification`, {
                status,
                ig_handle: igHandle,
                tiktok_handle: tiktokHandle,
            });
            setVerStatus(res.data?.verification?.status || status);
            setUser((prev) => (prev ? { ...prev, verified: res.data?.verified, verification: res.data?.verification } : prev));
        } catch (e) {
            console.error('Verification update failed', e);
            const missing = e?.response?.data?.missing;
            if (missing?.length) {
                alert(`${e.response.data.message || "Can't verify yet."}\nMissing: ${missing.join(', ')}`);
            } else {
                alert('Could not update verification. Please try again.');
            }
        } finally {
            setVerSaving(false);
        }
    };

    const profileMissing = verificationMissing(user);
    const profileComplete = !!user && profileMissing.length === 0;

    const formatDefaultDate = (date) => {
        if (!date) return null;
        const formattedDate = new Date(date);
        return isNaN(formattedDate.getTime()) ? null : formattedDate.toISOString().split('T')[0];
    };

    const optionsGender = [{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }]

    const handleGenderChange = (selectedOption) => {
        setGender(selectedOption ? selectedOption.value : "");
    }

    const selectedGenderOption = optionsGender.find(option => option.value === gender);

    const [isAdmin, setIsAdmin] = useState(null);

    return (
        <main>
            <TopNav />
            <div className="main-container">
                <SideBar />
                {deleteModal && (
                    <DeleteModal
                        handleDelete={handleDelete}
                        setDeleteModal={setDeleteModal}
                        modToDelete={userToDelete}
                        tableName={"USERS"}
                        isLoading={isLoading}
                    />
                )}
                <ConfirmModal
                    isOpen={photoReason !== null}
                    title="Take down this photo"
                    message={photoReason === 'silent'
                        ? 'The photo comes off their profile and they are told nothing.'
                        : `Pick a reason and ${user?.firstName || 'they'} will be told what to put up instead, in the app and by email where we have one.`}
                    confirmLabel="Take it down"
                    danger
                    confirmDisabled={!photoReason}
                    isLoading={takingDown}
                    onCancel={() => { if (!takingDown) { setPhotoReason(null); setGalleryTarget(null); } }}
                    onConfirm={() => (galleryTarget ? takeDownGalleryPhoto(galleryTarget, photoReason) : takeDownProfilePhoto(photoReason))}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 18px' }}>
                        {TAKEDOWN_REASONS.map((r) => (
                            <button
                                key={r.value}
                                type="button"
                                aria-pressed={photoReason === r.value}
                                disabled={takingDown}
                                onClick={() => setPhotoReason(r.value)}
                                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: takingDown ? 'not-allowed' : 'pointer', background: photoReason === r.value ? 'rgba(79,48,234,0.18)' : 'rgba(255,255,255,0.04)', border: `1px solid ${photoReason === r.value ? '#4F30EA' : 'rgba(255,255,255,0.10)'}`, color: '#fff' }}
                            >
                                <div style={{ fontSize: 14, fontWeight: 600 }}>{r.label}</div>
                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 3 }}>{r.hint}</div>
                            </button>
                        ))}
                    </div>
                </ConfirmModal>

                {banModal && (
                    <DeleteModal
                        handleDelete={handleBan}
                        setDeleteModal={setBanModal}
                        modToDelete={user}
                        title="Ban user"
                        message={
                            <>
                                <p className="delete-modal__modal-content-text">
                                    BAN {user?.firstName || 'this user'} {user?.lastName || ''}?
                                </p>
                                <p className="delete-modal__modal-content-text">
                                    Disables their account and blocks their phone number and
                                    device from ever signing up again. No email is sent.
                                </p>
                            </>
                        }
                        button="Ban"
                        isLoading={banLoading}
                    />
                )}
                <article className="page-content">
                    <section className="edit-user">
                        <div className="edit-user__top flex-wrap gap-1 pb-3">
                            <div className="edit-user__top-left">
                                <Link to="/users">
                                    <img
                                        className="edit-user__top-left-icon"
                                        src={chevronLeft}
                                        alt="Left pointing chevron"
                                    ></img>
                                </Link>
                                <h1 className="edit-user__top-left-heading">Editing User</h1>
                            </div>
                            <div>
                                <Button 
                                    label='Delete User'
                                    className="btn-outline-danger" 
                                    onClick={() => handleDeleteUser(user)} 
                                    iconifyIconName='solar:trash-bin-trash-outline' 
                                />
                            </div>
                        </div>

                        {user && (
                          <p style={{ margin: '0 0 18px', fontSize: 12, color: 'rgba(255,255,255,.45)', fontFamily: 'monospace', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                            <span
                              onClick={() => handleCopyId(user._id, 'Mongo ID')}
                              title="Click to copy"
                              style={{ cursor: user._id ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              ID: {user._id || '—'}
                              {user._id && <span className="iconify icon" data-icon="solar:copy-outline"></span>}
                            </span>
                            <span
                              onClick={() => handleCopyId(user.id, 'Firebase UID')}
                              title="Click to copy"
                              style={{ cursor: user.id ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              Firebase: {user.id || '—'}
                              {user.id && <span className="iconify icon" data-icon="solar:copy-outline"></span>}
                            </span>
                          </p>
                        )}

                        {user && (
                          <div style={{ background: user.banned_at ? 'rgba(229,62,62,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${user.banned_at ? 'rgba(229,62,62,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, padding: 18, marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: user.banned_at ? 6 : 0 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Account Status</h3>
                                {user.banned_at ? (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(229,62,62,.16)', color: '#f87171' }}>🚫 Banned</span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(198,241,53,.16)', color: '#C6F135' }}>● Active</span>
                                )}
                              </div>
                              {user.banned_at && (
                                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                                  {new Date(user.banned_at).toLocaleString()} — {user.ban_reason || 'No reason recorded'}
                                </p>
                              )}
                            </div>
                            {!user.banned_at && (
                              <Button
                                label="Ban user"
                                className="btn-outline-danger"
                                onClick={() => setBanModal(true)}
                                iconifyIconName="solar:forbidden-circle-outline"
                              />
                            )}
                          </div>
                        )}

                        {user && (
                          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 22 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                              <div>
                                <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600, color: '#fff' }}>Sign-in link</h3>
                                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                                  {user.banned_at || user.deleted_at
                                    ? 'Not available for a banned or deleted account.'
                                    : `Texts a one-time link to ${user.phone || "this account's mobile number"}. It opens the app on a create-password screen, marks the phone verified, and works once for 7 days.`}
                                </p>
                              </div>
                              {!user.banned_at && !user.deleted_at && (
                                <Button
                                  label="Send sign-in link"
                                  className="btn-outline-light"
                                  onClick={handleSendLoginLink}
                                  showLoading={loginLinkSending}
                                  iconifyIconName="solar:link-outline"
                                />
                              )}
                            </div>
                            {loginLinkResult?.url && (
                              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <code style={{ fontSize: 12, color: '#C6F135', wordBreak: 'break-all' }}>{loginLinkResult.url}</code>
                                <Button
                                  label="Copy"
                                  className="btn-outline-light"
                                  onClick={() => handleCopyId(loginLinkResult.url, 'Link')}
                                  iconifyIconName="solar:copy-outline"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {isVerifiedDj && (
                          <div style={{ background: djConfirm ? 'rgba(229,62,62,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${djConfirm ? 'rgba(229,62,62,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, padding: 18, marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Verified DJ</h3>
                                <span style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>On</span>
                              </div>
                              <p style={{ margin: 0, fontSize: 12, color: djConfirm ? '#fff' : 'rgba(255,255,255,.55)' }}>
                                {djConfirm
                                  ? djConfirmText
                                  : 'Has the Verified DJ tag and guestlist tools. Removing it keeps their account and past plans.'}
                              </p>
                            </div>
                            {user.banned_at || user.deleted_at ? null : djConfirm ? (
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <Button label="Cancel" className="btn-outline-light" onClick={() => setDjConfirm(false)} disabled={djRemoving} />
                                <Button label="Remove" className="btn-outline-danger" onClick={handleRemoveDj} showLoading={djRemoving} disabled={djRemoving} iconifyIconName="solar:trash-bin-trash-outline" />
                              </div>
                            ) : (
                              <Button label="Remove Verified DJ" className="btn-outline-danger" onClick={() => setDjConfirm(true)} iconifyIconName="solar:music-note-outline" />
                            )}
                          </div>
                        )}

                        {user && (
                          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Verified Promoter</h3>
                                {isVerifiedPromoter ? (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(198,241,53,.16)', color: '#C6F135' }}>On</span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.6)' }}>Off</span>
                                )}
                              </div>
                              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                                Lets this account post plans and run guestlists without applying in the app. Needed before weekly nights can be added below.
                              </p>
                            </div>
                            {!user.banned_at && !user.deleted_at && (
                              <Button
                                label={isVerifiedPromoter ? 'Remove Verified Promoter' : 'Make Verified Promoter'}
                                className={isVerifiedPromoter ? 'btn-outline-danger' : 'btn-outline-light'}
                                onClick={() => handlePromoterGrant(!isVerifiedPromoter)}
                                showLoading={promoterSaving}
                                iconifyIconName="solar:star-outline"
                              />
                            )}
                          </div>
                        )}

                        {user && (
                          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 22 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Weekly nights</h3>
                              {nightsEligibility && (
                                nightsEligibility.can_repost ? (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(198,241,53,.16)', color: '#C6F135' }}>Reposts weekly</span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(240,161,60,.16)', color: '#f0a13c' }}>Posts once</span>
                                )
                              )}
                            </div>
                            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                              Each night posts as a plan under this account for its next date and, while the account can use promoter plans, reposts for the following week when it ends. Guests join from the app; this account accepts or declines them there.
                            </p>
                            {nightsEligibility && !nightsEligibility.verified_promoter && (
                              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#f0a13c' }}>Make this account a Verified Promoter first.</p>
                            )}
                            {nightsEligibility && nightsEligibility.verified_promoter && !nightsEligibility.plans_access && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                                <p style={{ margin: 0, fontSize: 12, color: '#f0a13c' }}>Plans are off for this account, so nights post once and do not repost. Turn plans on for this account to make them weekly.</p>
                                <Button label="Turn plans on" className="btn-outline-light" onClick={() => handlePlansAccess(true)} showLoading={plansAccessSaving} iconifyIconName="solar:check-circle-outline" />
                              </div>
                            )}

                            {nightsLoading ? (
                              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>Loading nights</p>
                            ) : nights.length ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                                {nights.map((n) => (
                                  <div key={n.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', opacity: n.active ? 1 : 0.5 }}>
                                    <div style={{ fontSize: 13, color: '#fff' }}>
                                      <strong>{DAYS[n.weekday]}</strong> · {n.venue_name} · {n.kind} · {fromMinutes(n.start)}{n.end !== null && n.end !== undefined ? ` to ${fromMinutes(n.end)}` : ''}{n.closes !== null && n.closes !== undefined ? ` · list closes ${fromMinutes(n.closes)}` : ''}
                                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>
                                        {!n.active ? `Stopped ${n.stopped_at ? new Date(n.stopped_at).toLocaleDateString() : ''}` : n.current_plan_ends_at && new Date(n.current_plan_ends_at) > new Date() ? `Plan up until ${new Date(n.current_plan_ends_at).toLocaleString()}` : 'Next plan posts with the weekly job'}
                                      </div>
                                    </div>
                                    {n.active && (
                                      <Button label="Stop" className="btn-outline-danger" onClick={() => handleStopNight(n)} showLoading={nightStopping === n.id} iconifyIconName="solar:stop-circle-outline" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p style={{ margin: '0 0 14px', fontSize: 12, color: 'rgba(255,255,255,.4)' }}>No weekly nights yet.</p>
                            )}

                            {!user.banned_at && !user.deleted_at && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
                                <div onFocus={loadVenueOptions} onMouseEnter={loadVenueOptions}>
                                  <SelectForm
                                    label="Venue"
                                    options={venueOptions || []}
                                    value={nightForm.venue}
                                    onChange={(opt) => setNightForm((f) => ({ ...f, venue: opt, venueName: '' }))}
                                    onRemove={() => setNightForm((f) => ({ ...f, venue: null }))}
                                  />
                                </div>
                                <InputForm label="Or a place not on ZONA" placeholder="Venue name" type="text" id="night-venue-name" name="night-venue-name" value={nightForm.venueName} onChange={(e) => setNightForm((f) => ({ ...f, venueName: e.target.value, venue: null }))} />
                                <SelectForm label="Day" options={DAYS.map((d, i) => ({ value: i, label: d }))} value={{ value: Number(nightForm.weekday), label: DAYS[Number(nightForm.weekday)] }} onChange={(opt) => setNightForm((f) => ({ ...f, weekday: opt.value }))} />
                                <SelectForm label="Kind" options={['Guestlist', 'Table', 'Dinner', 'Party', 'Event'].map((k) => ({ value: k, label: k }))} value={{ value: nightForm.kind, label: nightForm.kind }} onChange={(opt) => setNightForm((f) => ({ ...f, kind: opt.value }))} />
                                <InputForm label="Starts" placeholder="23:00" type="text" id="night-start" name="night-start" value={nightForm.start} onChange={(e) => setNightForm((f) => ({ ...f, start: e.target.value }))} />
                                <InputForm label="Ends" placeholder="05:00" type="text" id="night-end" name="night-end" value={nightForm.end} onChange={(e) => setNightForm((f) => ({ ...f, end: e.target.value }))} />
                                <InputForm label="Guestlist closes" placeholder="23:30" type="text" id="night-closes" name="night-closes" value={nightForm.closes} onChange={(e) => setNightForm((f) => ({ ...f, closes: e.target.value }))} />
                                <InputForm label="Flyer (https link, optional)" placeholder="https://" type="text" id="night-image" name="night-image" value={nightForm.imageUrl} onChange={(e) => setNightForm((f) => ({ ...f, imageUrl: e.target.value }))} />
                                <Button
                                  label="Add night"
                                  className="btn-outline-light"
                                  onClick={handleAddNight}
                                  showLoading={nightSaving}
                                  iconifyIconName="solar:calendar-add-outline"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {user && (
                          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Discover Row</h3>
                                {user.discover_row_hidden ? (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(240,161,60,.16)', color: '#f0a13c' }}>Hidden</span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(198,241,53,.16)', color: '#C6F135' }}>Visible</span>
                                )}
                              </div>
                              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                                The rotating row of people shown on other members' profile screens. This does not touch the full Discover page or search.
                              </p>
                            </div>
                            <Button
                              label={user.discover_row_hidden ? 'Restore to row' : 'Hide from row'}
                              className={user.discover_row_hidden ? 'btn-outline-light' : 'btn-outline-danger'}
                              onClick={handleToggleDiscoverRowHidden}
                              showLoading={discoverRowSaving}
                              iconifyIconName={user.discover_row_hidden ? 'solar:eye-outline' : 'solar:eye-closed-outline'}
                            />
                          </div>
                        )}

                        {user && (
                          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 22 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Identity Verification</h3>
                              {verStatus === 'verified' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(198,241,53,.16)', color: '#C6F135' }}>✓ Verified</span>}
                              {verStatus === 'pending' && <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(96,165,250,.16)', color: '#93c5fd' }}>◐ Pending review</span>}
                              {verStatus === 'rejected' && <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(248,113,113,.16)', color: '#f87171' }}>✕ Rejected</span>}
                              {(verStatus === 'none' || !verStatus) && <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.5)' }}>○ Not verified</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                              <div style={{ flex: '1 1 220px' }}>
                                <label style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', display: 'block', marginBottom: 5 }}>Instagram handle</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <input value={igHandle} onChange={e => setIgHandle(e.target.value.replace(/^@/, ''))} placeholder="handle" style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 11px', color: '#fff', fontSize: 13 }} />
                                  {igHandle && <a href={`https://instagram.com/${igHandle.replace(/^@/, '')}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', padding: '0 12px', borderRadius: 8, background: 'rgba(157,139,255,0.15)', color: '#9d8bff', fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>Open ↗</a>}
                                </div>
                              </div>
                              <div style={{ flex: '1 1 220px' }}>
                                <label style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', display: 'block', marginBottom: 5 }}>TikTok handle</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <input value={tiktokHandle} onChange={e => setTiktokHandle(e.target.value.replace(/^@/, ''))} placeholder="handle" style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 11px', color: '#fff', fontSize: 13 }} />
                                  {tiktokHandle && <a href={`https://tiktok.com/@${tiktokHandle.replace(/^@/, '')}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', padding: '0 12px', borderRadius: 8, background: 'rgba(157,139,255,0.15)', color: '#9d8bff', fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>Open ↗</a>}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                              <button type="button" disabled={verSaving || !profileComplete} title={!profileComplete ? 'Personal info must be filled in to verify' : ''} onClick={() => handleSetVerification('verified')} style={{ borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: profileComplete ? 'pointer' : 'not-allowed', border: 'none', background: 'linear-gradient(135deg,#5a3cf0,#C6F135)', color: '#08110a', opacity: profileComplete ? 1 : 0.4 }}>{verSaving ? 'Saving…' : '✓ Approve & verify'}</button>
                              <button type="button" disabled={verSaving} onClick={() => handleSetVerification('rejected')} style={{ borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(248,113,113,.4)', color: '#f87171' }}>Reject</button>
                              {verStatus === 'verified' && <button type="button" disabled={verSaving} onClick={() => handleSetVerification('none')} style={{ borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)' }}>Un-verify</button>}
                              {profileComplete
                                ? <span style={{ fontSize: 11, color: 'rgba(198,241,53,.75)' }}>✓ Ready to verify · compare handles + photos before approving.</span>
                                : <span style={{ fontSize: 11, color: '#f0a13c' }}>⚠ Can't verify yet. Missing: {profileMissing.join(', ')}</span>}
                            </div>
                          </div>
                        )}

                        <form onSubmit={handleFormSubmit} className="edit-user__form">
                            <div className="edit-user__form-first">
                                {/* Admin dropdown hidden: granting admin access is a sensitive
                                    privilege, kept out of the routine edit form. The existing
                                    admin flag is preserved on save via isAdmin below. */}

                                <div>
                                    <div>
                                        <InputForm
                                            placeholder="Name"
                                            label="Name"
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={firstName}
                                            onChange={(event) =>
                                                setFirstName(event.target.value)
                                            }
                                            error={errors.firstName}
                                        />
                                    </div>

                                    <div>
                                        <InputForm
                                            placeholder="Last Name"
                                            label="Last Name"
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={lastName}
                                            onChange={(event) =>
                                                setLastName(event.target.value)
                                            }
                                            error={errors.lastName}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div>
                                        <InputForm
                                            placeholder="Phone"
                                            label="Phone"
                                            type="text"
                                            id="phone"
                                            name="phone"
                                            value={phone}
                                            onChange={(event) =>
                                                setPhone(event.target.value)
                                            }
                                            error={errors.phone}
                                        />
                                    </div>
                                    <div>
                                        <InputForm
                                            placeholder="Email"
                                            label="Email"
                                            type="text"
                                            id="email"
                                            name="email"
                                            value={email}
                                            onChange={(event) =>
                                                setEmail(event.target.value)
                                            }
                                            error={errors.email}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div>
                                        <SelectForm
                                            label="Gender"
                                            options={optionsGender}
                                            value={selectedGenderOption}
                                            onChange={handleGenderChange}
                                            error={errors.gender}
                                        />
                                    </div>

                                    <div>
                                        <FormDatePicker
                                            onBirthdaySelect={handleBirthdaySelect}
                                            defaultDate={formatDefaultDate(birthday)}
                                        />
                                    </div>
                                </div>


                                <div>
                                    <Button 
                                        type="submit" 
                                        primaryBtn
                                        label='Update Information'
                                        iconifyIconName='ic:round-check'
                                        showLoading={updating}
                                    />
                                </div>
                            </div>
                        </form>

                        {user?.photo_url && (
                            <div style={{ marginTop: 32 }}>
                                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Profile photo</h2>
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                                    The user's avatar. Click to view full size; take it down if it violates policy.
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <img src={user.photo_url} alt="Profile" onClick={() => setLightboxUrl(user.photo_url)} style={{ width: 120, height: 120, borderRadius: 60, objectFit: 'cover', display: 'block', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }} />
                                    <button type="button" onClick={() => setPhotoReason('')} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #E11D48', background: 'transparent', color: '#E11D48', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                        Take down
                                    </button>
                                </div>
                            </div>
                        )}
                        <div style={{ marginTop: 32 }}>
                            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Photos</h2>
                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                                The user's uploaded photos. Click to view full size; take down anything that violates policy.
                            </p>
                            {(!gallery || gallery.length === 0) ? (
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>This user hasn't uploaded any photos.</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                                    {gallery.map((photo) => (
                                        <div key={photo.id} style={{ width: 130 }}>
                                            <img
                                                src={photo.thumb_uri || photo.uri}
                                                alt="User gallery"
                                                onClick={() => setLightboxUrl(photo.uri)}
                                                style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 8, display: 'block', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleGalleryRemove(photo.id)}
                                                style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 8, border: '1px solid #E11D48', background: 'transparent', color: '#E11D48', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                            >
                                                Take down
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </article>
            </div>

            {lightboxUrl && (
                <div
                    onClick={() => setLightboxUrl(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}
                >
                    <img src={lightboxUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} />
                </div>
            )}
        </main>
    );
}

// Keyed on the URL's user so switching users remounts the page and every piece of state resets.
const EditUsersRoute = () => {
    const { userID } = useParams();
    return <EditUsers key={userID} />;
};

export default EditUsersRoute;
